<script lang="ts" setup>
import DefinitionListSection from '../../DefinitionListSection.vue';
import HeadlineListSection from '../../HeadlineListSection.vue';
import ParagrahNote from '../../ParagrahNote.vue';
import ParagrahNotice from '../../ParagrahNotice.vue';
import ScrollTable from '../../ScrollTable.vue';
import WhereToConsultCard from '../../WhereToConsult/WhereToConsultCard.vue';
import CreditorGroupInsurancesImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/05_creditor_group_insurance.png/05_creditor_group_insurance.webp';
import PinchImageModal from '~theme/components/organisms/PinchImageModal.vue';

defineProps<{
  imageNumber: number;
}>();
</script>

<template>
  <WhereToConsultCard
    title="住宅ローンのおすすめの選び方"
    :image-number="imageNumber"
  >
    <p>
      多くの金融機関からさまざまな住宅ローンの商品が出ていますが、目的ごとにおすすめの選び方をご紹介します。自分にあった住宅ローンを探す際の参考にしてください。
    </p>
    <HeadlineListSection>
      <li>
        <a href="#card3Headline1">金利を抑えたい方は「変動金利」を選ぶ</a>
      </li>
      <li>
        <a href="#card3Headline2"
          >金利変動リスクを抑えたい場合は「固定金利」を選ぶ</a
        >
      </li>
      <li>
        <a href="#card3Headline3">審査が不安な方は「フラット35」を選ぶ</a>
      </li>
      <li>
        <a href="#card3Headline4"
          >万一にしっかり備えたい方は団信の保障内容で選ぶ</a
        >
      </li>
      <li>
        <a href="#card3Headline5">窓口で相談したい方は店舗を持つ銀行を選ぶ</a>
      </li>
    </HeadlineListSection>

    <h3 id="card3Headline1">金利を抑えたい方は「変動金利」を選ぶ</h3>

    <ScrollTable>
      <thead>
        <tr>
          <th colspan="2">変動金利タイプの特徴</th>
        </tr>
      </thead>
      <tr>
        <th>メリット</th>
        <td>
          <ul>
            <li>固定金利よりも金利が低い傾向にある</li>
            <li>変動とはいえ急激に金利が変動することはない</li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>デメリット</th>
        <td>
          <ul>
            <li>将来的に金利が上がる可能性があり、返済計画を立てにくい</li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>金利の目安</th>
        <td>年0.3〜0.7%程 <ParagrahNotice>(2024年6月時点)</ParagrahNotice></td>
      </tr>
    </ScrollTable>

    <ParagrahNote>
      適用金利は申込内容や審査結果等に応じて変動します。
    </ParagrahNote>
    <p>
      変動金利とは、市場金利に応じて定期的に変動する金利のことです。変動金利の金利は半年に1度、返済額は5年に一度見直しが行われますが、金利上昇幅は25%までに抑えられています。
    </p>
    <p>
      変動金利タイプの大きなメリットは、固定金利タイプに比べて金利が低い傾向にあることです。現在は年0.5％以下の金利が適用されるケースも珍しくないため、金利を抑えたい方は変動金利タイプを選ぶと良いでしょう。
    </p>
    <p>
      ただし、金利の上昇によって返済総額が増えるリスクがあります。繰り上げ返済をすることで金利上昇リスクを抑えることができるため、繰り上げ返済のための資金の確保が可能か、どこまでの金利上昇（返済額）に耐えられるかなど、あらかじめシミュレーションしておきましょう。
    </p>

    <h3 id="card3Headline2">
      金利変動リスクを抑えたい場合は「固定金利」を選ぶ
    </h3>

    <ScrollTable>
      <thead>
        <tr>
          <th colspan="2">固定金利タイプの特徴</th>
        </tr>
      </thead>
      <tr>
        <th>メリット</th>
        <td>
          <ul>
            <li>固定期間中は金利が変わらないので返済計画が立てやすい</li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>デメリット</th>
        <td>
          <ul>
            <li>変動金利よりも金利が高い傾向にある</li>
            <li>
              金利が下降傾向でも借入時の金利が適用され、返済額が減ることはない
            </li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>金利の目安</th>
        <td>
          (10年固定金利の場合) 年1.2%程〜
          <ParagrahNotice>(2024年6月時点)</ParagrahNotice>
        </td>
      </tr>
    </ScrollTable>

    <ParagrahNote>
      適用金利は申込内容や審査結果等に応じて変動します。
    </ParagrahNote>
    <p>
      3年間、10年間など、一定期間金利が変動しないものを固定金利と呼びます。35年の長期間固定金利が適用されるものもあり、固定金利期間が長いほど金利は高くなる傾向です。
    </p>
    <p>
      固定金利タイプの住宅ローンなら、固定金利期間中は金利が上昇しても返済額が増える心配はありません。ただし、逆に金利が下落した場合も当初の金利が継続し、返済額が減ることはない点に注意が必要です。
    </p>
    <p>
      固定金利は「新発10年国債の利回り」に連動しており、変動幅の拡大により、直近の住宅ローンの固定金利は少しずつ上昇傾向にあります。
    </p>

    <h3 id="card3Headline3">審査が不安な方は「フラット35」を選ぶ</h3>

    <ScrollTable>
      <thead>
        <tr>
          <th colspan="2">フラット35の特徴</th>
        </tr>
      </thead>
      <tr>
        <th>メリット</th>
        <td>
          <ul>
            <li>全期間固定金利なので返済計画が立てやすい</li>
            <li>保証人や保証料が不要</li>
            <li>年収や勤続年数による借入制限がない</li>
            <li>団体信用生命保険の加入が任意</li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>デメリット</th>
        <td>
          <ul>
            <li>変動金利よりも金利が高い傾向にある</li>
            <li>
              金利が下降傾向でも借入時の金利が適用され、返済額が減ることはない
            </li>
            <li>住宅に審査基準が設けられている</li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>金利の目安</th>
        <td>
          (10年固定金利の場合) 年1.8%程〜
          <ParagrahNotice>(2024年6月時点)</ParagrahNotice>
        </td>
      </tr>
    </ScrollTable>

    <ParagrahNote>
      適用金利は申込内容や審査結果等に応じて変動します。
    </ParagrahNote>
    <p>
      フラット35は、独立行政法人住宅金融支援機構が民間の金融機関と提携して提供している、全期間固定金利型の住宅ローンです。返済期間は15年から35年まで1年単位で選べます。
    </p>
    <p>
      フラット35は、利用条件に最低年収などの制限がないため、総返済負担率の基準を満たせば、年収に関係なく借入できます。
    </p>
    <DefinitionListSection>
      <dt>住宅ローン繰り上げ返済の概要</dt>
      <dd>
        <ul>
          <li>年収400万円未満：30％以下</li>
          <li>年収400万円以上：35％以下</li>
        </ul>
      </dd>
    </DefinitionListSection>
    <p>
      また、審査には勤続年数の条件も設けられていないため、現在勤務している会社に入社間もない方や個人事業主の方でも申込が可能です。
    </p>
    <p>
      団体信用生命保険の加入も任意なため、健康に不安があったり、他の住宅ローンの審査に不安がある方はフラット35を検討しましょう。
    </p>
    <p>
      ただし、フラット35は購入する住宅に借入要件があり、住宅が住宅金融支援機構が定める技術基準に適合したうえで、床面積が一定以上でなければ、フラット35の借り入れはできない点に注意が必要です。
    </p>

    <h3 id="card3Headline4">万一にしっかり備えたい方は団信の保障内容で選ぶ</h3>
    <p>
      ほとんどの住宅ローンでは、団体信用生命保険（団信）への加入が契約の必須条件です。団信に加入すると、契約者が死亡・高度障害状態になった場合に保険金で住宅ローンが完済されるため、万一の際に返済が滞り家を失う事態を避けられます。
    </p>
    <PinchImageModal :src="CreditorGroupInsurancesImg" />
    <p>
      民間の銀行の住宅ローンの場合、死亡・高度障害を保障する一般的な団信の保険料は金利に含まれており、基本的に無料です。なかには金利上乗せなしでプラスアルファの保障が付帯している住宅ローンもあります。
    </p>
    <p>
      団信にはガン保障や介護保障、3大疾病保障などのオプションを付けられますが、保障の付加に金利が上乗せされる場合は、本当に必要な保障なのか慎重に考えましょう。一般的な生命保険と違い、団信は中途解約もできないので、自分にとって必要な最低限の保障にとどめることが大切です。
    </p>
    <ScrollTable>
      <thead>
        <tr>
          <th colspan="4">団体信用生命保険の保障内容の一例</th>
        </tr>
        <tr>
          <th>事由</th>
          <th>詳細</th>
          <th>保障内容</th>
          <th>一般的な上乗せ金利※</th>
        </tr>
      </thead>
      <tr>
        <th>死亡・高度障害</th>
        <td>死亡または高度障害に該当したとき</td>
        <td rowspan="3">住宅ローン残高が0円</td>
        <td>なし</td>
      </tr>
      <tr>
        <th>がん</th>
        <td>所定の悪性新生物と診断確定されたとき</td>
        <td>+0.1%</td>
      </tr>
      <tr>
        <th>3大疾病</th>
        <td>
          <ul>
            <li>所定の悪性新生物と診断確定されたとき</li>
            <li>
              脳卒中、急性心筋梗塞で所定の手術を受けた時または所定の状態が60日以上継続したとき
            </li>
          </ul>
        </td>
        <td>+0.2%</td>
      </tr>
      <tr>
        <th rowspan="2">全ての病気・ケガ</th>
        <td>所定の就業不能状態が3ヶ月を超えて継続したとき</td>
        <td>毎月の住宅ローン返済額を保障</td>
        <td rowspan="2">+0.3%</td>
      </tr>
      <tr>
        <td style="background-color: unset">
          所定の就業不能状態が12ヶ月を超えて継続した時
        </td>
        <td>住宅ローン残高が0円</td>
      </tr>
    </ScrollTable>

    <ParagrahNote>
      ※融資実行の時期やお借入れ金融機関によって、上記の上乗せ金利とは異なる場合があります。
    </ParagrahNote>

    <h3 id="card3Headline5">窓口で相談したい方は店舗を持つ銀行を選ぶ</h3>
    <p>
      メガバンクは全国に店舗を持ちますが、ネット銀行は実店舗を持たないことが多く、web上や電話でやり取りをすることになります。
    </p>
    <p>
      もし直接窓口で相談や契約を進めたい場合は、店舗を持つ金融機関を選びましょう。
    </p>
  </WhereToConsultCard>
</template>

<style module lang="scss"></style>
